import React, { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Plus, Trash, Edit, Save } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/components/ui/use-toast';

interface SessionEditorProps {
  sessions: any[];
  onChange: (sessions: any[]) => void;
  userId: string | undefined;
}

export default function SessionEditor({ sessions, onChange, userId }: SessionEditorProps) {
  const { toast } = useToast();
  const [draft, setDraft] = useState({
    id: null as string | null,
    title: '',
    description: '',
    price_cents: '',
    duration_min: '',
    price_mode: 'fixed', // 'fixed' or 'per_person'
    group_min: '',
    group_max: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const resetDraft = () => {
    setDraft({
      id: null,
      title: '',
      description: '',
      price_cents: '',
      duration_min: '',
      price_mode: 'fixed',
      group_min: '',
      group_max: ''
    });
    setEditingId(null);
  };

  const handleAddOrUpdateSession = async () => {
    if (!userId || !draft.title || !draft.price_cents || !draft.duration_min) {
      toast({
        title: "Validation Error",
        description: "Title, price, and duration are required.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        user_id: userId,
        title: draft.title,
        description: draft.description || null,
        price_cents: Number(draft.price_cents) * 100,
        duration_min: Number(draft.duration_min),
        price_mode: draft.price_mode,
        group_min: draft.group_min ? Number(draft.group_min) : null,
        group_max: draft.group_max ? Number(draft.group_max) : null,
      };

      let data;
      let error;

      if (editingId) {
        const result = await supabase
          .from('trainer_session_types')
          .update(payload)
          .eq('id', editingId)
          .select()
          .single();
        data = result.data;
        error = result.error;
      } else {
        const result = await supabase
          .from('trainer_session_types')
          .insert(payload)
          .select()
          .single();
        data = result.data;
        error = result.error;
      }

      if (error) throw error;

      if (editingId) {
        onChange(sessions.map(s => (s.id === data.id ? data : s)));
        toast({ title: "Success", description: "Session updated successfully!", variant: "default" });
      } else {
        onChange([...sessions, data]);
        toast({ title: "Success", description: "Session added successfully!", variant: "default" });
      }
      resetDraft();
    } catch (error) {
      console.error('Error saving session:', error);
      toast({
        title: "Error",
        description: "Failed to save session.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSession = async (sessionId: string) => {
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('trainer_session_types')
        .delete()
        .eq('id', sessionId);

      if (error) throw error;

      onChange(sessions.filter(s => s.id !== sessionId));
      toast({ title: "Success", description: "Session deleted successfully!", variant: "default" });
    } catch (error) {
      console.error('Error deleting session:', error);
      toast({
        title: "Error",
        description: "Failed to delete session.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditClick = (session: any) => {
    setDraft({
      id: session.id,
      title: session.title,
      description: session.description || '',
      price_cents: (session.price_cents / 100).toString(),
      duration_min: session.duration_min.toString(),
      price_mode: session.price_mode || 'fixed',
      group_min: session.group_min?.toString() || '',
      group_max: session.group_max?.toString() || ''
    });
    setEditingId(session.id);
  };

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-white">Session Types</h3>

      {sessions.length === 0 ? (
        <div className="text-center py-6 bg-dark-300 rounded-xl border border-dark-400">
          <p className="text-dark-800">No session types added yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sessions.map(s => (
            <div
              key={s.id}
              className="bg-dark-300 rounded-lg p-4 flex items-center justify-between border border-dark-400"
            >
              <div>
<h4 className="text-sm font-medium text-white">{s.title}</h4>
                <p className="text-dark-800 text-sm">
                  ${(s.price_cents / 100).toFixed(0)} / {s.duration_min} min
                  {s.price_mode === 'per_person' && ' per person'}
                  {s.group_min && s.group_max && ` (${s.group_min}-${s.group_max} people)`}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  onClick={() => handleEditClick(s)}
                  variant="outline"
                  size="sm"
                  className="border-dark-400 text-white hover:bg-dark-400"
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  onClick={() => handleDeleteSession(s.id)}
                  variant="outline"
                  size="sm"
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  disabled={isSaving}
                >
                  <Trash className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-4 bg-dark-300 p-4 rounded-xl border border-dark-400">
        <h4 className="text-lg font-medium text-white">{editingId ? 'Edit Session' : 'Add New Session'}</h4>
        <div>
          <label htmlFor="session-title" className="block text-dark-800 mb-2">Title</label>
          <Input
            id="session-title"
            placeholder="e.g., 1-on-1 Coaching"
            value={draft.title}
            onChange={e => setDraft({ ...draft, title: e.target.value })}
            className="bg-dark-400 border-dark-500 text-white"
          />
        </div>
        <div>
          <label htmlFor="session-description" className="block text-dark-800 mb-2">Description (Optional)</label>
          <Textarea
            id="session-description"
            placeholder="Brief description of the session"
            value={draft.description}
            onChange={e => setDraft({ ...draft, description: e.target.value })}
            className="bg-dark-400 border-dark-500 text-white"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="session-price" className="block text-dark-800 mb-2">Price (USD)</label>
            <Input
              id="session-price"
              type="number"
              placeholder="e.g., 100"
              value={draft.price_cents}
              onChange={e => setDraft({ ...draft, price_cents: e.target.value })}
              className="bg-dark-400 border-dark-500 text-white"
            />
          </div>
          <div>
            <label htmlFor="session-duration" className="block text-dark-800 mb-2">Duration (Minutes)</label>
            <Input
              id="session-duration"
              type="number"
              placeholder="e.g., 60"
              value={draft.duration_min}
              onChange={e => setDraft({ ...draft, duration_min: e.target.value })}
              className="bg-dark-400 border-dark-500 text-white"
            />
          </div>
        </div>
        <div>
          <label htmlFor="price-mode" className="block text-dark-800 mb-2">Pricing Mode</label>
          <select
            id="price-mode"
            value={draft.price_mode}
            onChange={e => setDraft({ ...draft, price_mode: e.target.value as 'fixed' | 'per_person' })}
            className="w-full bg-dark-400 border border-dark-500 rounded-lg px-3 py-2 text-white"
          >
            <option value="fixed">Fixed Price</option>
            <option value="per_person">Per Person</option>
          </select>
        </div>
        {draft.price_mode === 'per_person' && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="group-min" className="block text-dark-800 mb-2">Min Group Size</label>
              <Input
                id="group-min"
                type="number"
                placeholder="e.g., 2"
                value={draft.group_min}
                onChange={e => setDraft({ ...draft, group_min: e.target.value })}
                className="bg-dark-400 border-dark-500 text-white"
              />
            </div>
            <div>
              <label htmlFor="group-max" className="block text-dark-800 mb-2">Max Group Size</label>
              <Input
                id="group-max"
                type="number"
                placeholder="e.g., 4"
                value={draft.group_max}
                onChange={e => setDraft({ ...draft, group_max: e.target.value })}
                className="bg-dark-400 border-dark-500 text-white"
              />
            </div>
          </div>
        )}
        <div className="flex justify-end space-x-2">
          {editingId && (
            <Button
              onClick={resetDraft}
              variant="outline"
              className="border-dark-400 text-white hover:bg-dark-400"
              disabled={isSaving}
            >
              Cancel Edit
            </Button>
          )}
          <Button
            onClick={handleAddOrUpdateSession}
            disabled={isSaving}
            className="bg-gold-400 hover:bg-gold-500 text-dark-50"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : editingId ? (
              <>
                <Save className="h-4 w-4 mr-2" />
                Update Session
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Add Session
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}